import { z } from "zod";
import type { TransformDefinition } from "./transforms";

/**
 * What a fallback may be, and what the transform guarantees it returns. Arrays
 * are deliberately EXCLUDED — see the note on batch mapping below.
 */
const PRESENT_SCALAR = z.union([z.string(), z.number(), z.boolean()]);

/**
 * A value a socket can carry that `fallback` may substitute for — the present
 * scalars plus the two absent spellings. Composed from {@link PRESENT_SCALAR}
 * rather than restating the three validators, so "what counts as a scalar here"
 * cannot drift between the argument and the result.
 */
const NULLABLE_SCALAR = z.union([PRESENT_SCALAR, z.null(), z.undefined()]);

/**
 * fallback — `(fallback value default)`. Yields `value` unless it is null or
 * undefined, in which case it yields `default`. Binary and eager: two args, both
 * already evaluated, no laziness (there are no closures in expressions).
 *
 * Deliberately NOT `coalesce`: nothing here collapses a list of candidates down
 * to the first present one. There is one possibly-absent value and one stand-in,
 * so the transform is binary and named for what it does.
 *
 * WHY THE ARGS REJECT ARRAYS. `evaluateTransform` decides between whole and
 * per-item evaluation **by fit**: args that satisfy the tuple whole are
 * evaluated whole, and only args that do not fit are mapped item-wise over the
 * batch. Observe steps emit each column as a parallel array, so a permissive
 * `z.unknown()` first arg would fit whole, and the transform would hand the
 * entire 5,000-item array straight through as "not null" — silently defaulting
 * nothing. Rejecting arrays is what forces the item-wise path, where each row's
 * own null is actually seen. `string_join`'s strict `z.string()` args work the
 * same way; the strictness is load-bearing, not fussiness.
 *
 * A dead branch is not an absent value: `evaluateAst` short-circuits to
 * `SOCKET_NOT_TAKEN` before any transform runs when an arg carries that
 * sentinel, so `fallback` never fires for a branch that was not taken. It
 * substitutes for missing DATA, not for a path the graph skipped.
 */
export const fallbackTransform = {
  name: "fallback",
  description:
    "Yields the first argument, unless it is null or undefined — then the second.",
  args: z.tuple([NULLABLE_SCALAR, PRESENT_SCALAR]),
  output: PRESENT_SCALAR,
  evaluate: ([value, whenAbsent]) =>
    value === null || value === undefined ? whenAbsent : value,
} satisfies TransformDefinition;
